import { createTheme, type Theme, type ThemeOptions, type TypographyStyle } from '@mui/material/styles'
import type { ThemeMode } from '@/theme/tokens/alias/brandTokens'
import { lightThemeTokens } from '@/theme/themes/lightTheme'
import { darkThemeTokens } from '@/theme/themes/darkTheme'
import type { ElevationScale, SpacingKey } from '@/theme/types'
import { lightElevationMap } from '@/theme/themes/lightTheme'
import { darkElevationMap } from '@/theme/themes/darkTheme'

const buildShadows = (elevation: ElevationScale): ThemeOptions['shadows'] => {
  const base = [...elevation]
  while (base.length < 25) {
    base.push(base[base.length - 1])
  }
  return base as ThemeOptions['shadows']
}

const createThemeOptions = (mode: ThemeMode): ThemeOptions => {
  const tokens = mode === 'dark' ? darkThemeTokens : lightThemeTokens
  const { semantic, base, components } = tokens
  const elevationMap = mode === 'dark' ? darkElevationMap : lightElevationMap

  return {
    palette: {
      mode,
      primary: {
        main: semantic.colors.primary,
        light: semantic.colors.primaryHover,
        dark: semantic.colors.primaryActive,
        contrastText: semantic.colors.textOnPrimary,
      },
      error: {
        main: semantic.colors.error,
      },
      success: {
        main: semantic.colors.success,
      },
      warning: {
        main: semantic.colors.warning,
      },
      info: {
        main: semantic.colors.info,
      },
      background: {
        default: semantic.colors.background,
        paper: semantic.surface.surface,
      },
      text: {
        primary: semantic.colors.textPrimary,
        secondary: semantic.colors.textSecondary,
        disabled: semantic.colors.textMuted,
      },
      action: {
        active: semantic.colors.iconPrimary,
        hover: semantic.interaction.hover,
        selected: semantic.interaction.selected,
        disabled: semantic.interaction.disabled,
        disabledBackground: semantic.interaction.disabled,
      },
    },
    typography: {
      fontFamily: base.typography.fontFamily,
      h1: { ...base.typography.h1 } as TypographyStyle,
      h2: { ...base.typography.h2 } as TypographyStyle,
      body1: { ...base.typography.body1 } as TypographyStyle,
      body2: { ...base.typography.body2 } as TypographyStyle,
    },
    shape: {
      borderRadius: base.radius.md,
    },
    spacing: (factor: number) => {
      const key = factor as SpacingKey
      return base.spacing[key] ?? factor * 8
    },
    shadows: buildShadows(base.elevation),
    zIndex: {
      modal: base.zIndex.modal,
      snackbar: base.zIndex.overlay,
      tooltip: base.zIndex.tooltip,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            height: components.button.height,
            paddingInline: components.button.paddingX,
            paddingBlock: components.button.paddingY,
            borderRadius: components.button.borderRadius,
            transition: `background-color ${base.motion.duration.normal}ms ${base.motion.easing.standard}`,
          },
          contained: {
            backgroundColor: components.button.background,
            color: semantic.colors.textOnPrimary,
            // Light theme: иконки на тёмных кнопках — светлые, как текст (иначе MuiSvgIcon даёт тёмный цвет)
            ...(mode === 'light' && {
              '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: 'inherit' },
              '& .MuiSvgIcon-root': { color: 'inherit' },
            }),
            '&:hover': {
              backgroundColor: components.button.backgroundHover,
              color: semantic.colors.textOnPrimary,
            },
            '&:disabled': {
              backgroundColor: semantic.interaction.disabled,
              color: semantic.colors.textMuted,
            },
          },
          outlined: {
            borderColor: semantic.colors.border,
            color: semantic.colors.textPrimary,
            '&:hover': {
              borderColor: semantic.colors.primary,
              backgroundColor: semantic.interaction.hover,
            },
          },
          text: {
            color: semantic.colors.textPrimary,
            '&:hover': {
              backgroundColor: semantic.interaction.hover,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: components.input.borderRadius,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: components.input.borderColor,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: components.input.borderColorFocus,
                borderWidth: 2,
              },
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: components.card.borderRadius,
            backgroundColor: semantic.surface.surface,
            color: semantic.colors.textOnSurface,
          },
          elevation1: {
            boxShadow: elevationMap.surface,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: components.card.borderRadius,
            boxShadow: elevationMap.surface,
            padding: components.card.padding,
            backgroundColor: semantic.surface.surface,
            color: semantic.colors.textOnSurface,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            backgroundColor: semantic.surface.surface,
            color: semantic.colors.textOnSurface,
            '&:hover': {
              backgroundColor: semantic.interaction.hover,
            },
            '&.Mui-selected': {
              backgroundColor: semantic.interaction.selected,
              color: semantic.colors.textOnSurface,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: semantic.colors.border,
            color: semantic.colors.textOnSurface,
          },
          head: {
            color: semantic.colors.textOnSurface,
            fontWeight: 600,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: base.radius.sm,
            color: semantic.colors.textOnSurface,
            backgroundColor: semantic.surface.surfaceElevated,
            '&.MuiChip-colorPrimary': {
              backgroundColor: semantic.colors.primary,
              color: semantic.colors.textOnPrimary,
            },
            '&.MuiChip-colorSuccess': {
              backgroundColor: semantic.colors.success,
              color: semantic.colors.textOnPrimary,
            },
            '&.MuiChip-colorError': {
              backgroundColor: semantic.colors.error,
              color: semantic.colors.textOnPrimary,
            },
            '&.MuiChip-colorWarning': {
              backgroundColor: semantic.colors.warning,
              color: semantic.colors.textOnPrimary,
            },
            '&.MuiChip-colorInfo': {
              backgroundColor: semantic.colors.info,
              color: semantic.colors.textOnPrimary,
            },
            '&.MuiChip-outlined': {
              backgroundColor: 'transparent',
              borderColor: semantic.colors.border,
              color: semantic.colors.textPrimary,
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: base.radius.md,
            color: semantic.colors.textOnSurface,
            '&.MuiAlert-standardError': {
              backgroundColor: mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
              color: semantic.colors.error,
            },
            '&.MuiAlert-standardWarning': {
              backgroundColor: mode === 'dark' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
              color: semantic.colors.warning,
            },
            '&.MuiAlert-standardSuccess': {
              backgroundColor: mode === 'dark' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)',
              color: semantic.colors.success,
            },
            '&.MuiAlert-standardInfo': {
              backgroundColor: mode === 'dark' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
              color: semantic.colors.info,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: semantic.colors.iconPrimary,
            '&:hover': {
              backgroundColor: semantic.interaction.hover,
            },
            '&:disabled': {
              color: semantic.colors.iconMuted,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: semantic.colors.textOnSurface,
            '&:hover': {
              backgroundColor: semantic.interaction.hover,
            },
            '&.Mui-selected': {
              backgroundColor: semantic.interaction.selected,
              color: semantic.colors.textOnSurface,
              '&:hover': {
                backgroundColor: semantic.interaction.selected,
              },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            color: semantic.colors.textOnSurface,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: semantic.colors.textOnSurface,
            backgroundColor: semantic.surface.surface,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: semantic.colors.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: semantic.colors.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: semantic.colors.primary,
            },
            '&.Mui-disabled': {
              backgroundColor: semantic.interaction.disabled,
              color: semantic.colors.textMuted,
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: base.radius.sm,
            backgroundColor: semantic.surface.surfaceElevated,
          },
          bar: {
            borderRadius: base.radius.sm,
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: semantic.colors.iconPrimary,
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            // Единый стиль хлебных крошек: кликабельные пункты — серые (не primary),
            // без ручных цветовых props на страницах.
            '& .MuiLink-root, & .MuiLink-root.MuiLink-colorInherit, & .MuiLink-root.MuiLink-colorPrimary, & .MuiLink-root.MuiLink-colorSecondary': {
              color: semantic.colors.textSecondary,
              textDecorationColor: 'currentColor',
              '&:hover': {
                color: semantic.colors.textPrimary,
              },
            },
            '& .MuiTypography-root, & .MuiTypography-root.MuiTypography-colorTextPrimary, & .MuiTypography-root.MuiTypography-colorInherit': {
              color: semantic.colors.textSecondary,
            },
            '& .MuiBreadcrumbs-separator': {
              color: semantic.colors.textMuted,
            },
          },
        },
      },
    },
  }
}

export const createAppTheme = (mode: ThemeMode = 'light'): Theme =>
  createTheme(createThemeOptions(mode))

